import { ReactNode, Component, ReactElement, lazy, Suspense } from 'react';
import _ from 'lodash';
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { SettingsList } from '@Primitive/SettingsList';
import { GaugeHelperHighlight, SettingsType } from '@Primitive/SettingsListType';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { WebThingPortType } from '@API/WebNodeTypes';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import type { LocalizationMap } from '@Primitive/Localization/Localization';

const PercentageGaugeBase = lazy(() => import("./PercentageGaugeBase"));
// =======================================================================================================

export interface PercentageGaugeData {
    PercentageGaugeSettings: PercentageGaugeSettings;
}

export interface PercentageGaugeSettings {
    Color: string;
    highlights: GaugeHelperHighlight[];
    decimalPlaces: number;
    enableDecimal: boolean;
    reverseMode: boolean;
    negativeLogic: boolean;
}

// =======================================================================================================
interface PercentageGaugeWidgetSettingsState {
    widgetData: PercentageGaugeData;
}

class PercentageGaugeWidgetSettings extends Component<IWidgetSettingsProps<PercentageGaugeData>, PercentageGaugeWidgetSettingsState> {
    constructor(props: IWidgetSettingsProps<PercentageGaugeData>) {
        super(props);

        this.state = { widgetData: _.cloneDeep(this.props.WidgetData) };
        this.saveData = this.saveData.bind(this);
    }
    // ---------------------------------------------------------------------------------------------------
    saveData() {
        if (isDefined(this.props.onWidgetDataUpdate))
            this.props.onWidgetDataUpdate(this.state.widgetData);
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        return <SettingsList
            AutoSave={true}
            Data={this.state.widgetData}
            SettingsMap={[
                {
                    name: '',
                    elements: [{
                        type: SettingsType.Number,
                        label: 'Decimal Places',
                        dataPath: 'PercentageGaugeSettings.decimalPlaces',
                        minValue: 0,
                        maxValue: 20,
                        oneLine: true,
                        tooltip: 'Set the number of decimal places.'
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Color',
                        dataPath: 'PercentageGaugeSettings.Color',
                        tooltip: "Sets gauge's color"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Decimal Highlights',
                        dataPath: 'PercentageGaugeSettings.enableDecimal',
                        tooltip: 'Whether to enable decimal values in from and to values of highlights.'
                    },
                    {
                        type: SettingsType.Highlights,
                        label: 'Highlights',
                        dataPath: 'PercentageGaugeSettings.highlights',
                        tooltip: 'Define different colors for value ranges.',
                        icon: <i className="fas fa-pen" style={{ fontSize: 16 }} />,
                        enableDecimal: 'PercentageGaugeSettings.enableDecimal',
                        minValue: 0,
                        maxValue: 100
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Counter-clockwise',
                        dataPath: 'PercentageGaugeSettings.reverseMode',
                        tooltip: 'Make the animation of the gauge counter-clockwise.'
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Negative Logic',
                        dataPath: 'PercentageGaugeSettings.negativeLogic',
                        tooltip: 'Whether to enable show absolute value of value on the circle'
                    }]
                }]}
            onSaveData={(data) => {
                this.setState(
                    { widgetData: data },
                    () => this.saveData());
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}
// =======================================================================================================
// Gauge Description interface
export const enum PercentageGaugePortKeys {
    Data = "Data"
}
export var PercentageGauge = (props: WidgetsThingsStructs.PartialIWidgetThingsProps<PercentageGaugeData>) => {
    return <Suspense fallback={<LazyLoadingAnimation />}>
        <PercentageGaugeBase {...props as any} />
    </Suspense>
}
export class PercentageGaugeWidget extends IWidgetThingsClass<PercentageGaugeData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): ReactNode {
        return <PercentageGauge />
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <PercentageGaugeWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [];
        ports.push(new WebThingPort(
            {
                Name: "Data",
                PortKey: PercentageGaugePortKeys.Data,
                Type: Plegma.ePortType.Decimal,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ""
            },
            WebThingPortType.Data));

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): PercentageGaugeData {
        return {
            PercentageGaugeSettings: {
                Color: "rgba(61, 160, 141, 1)",
                highlights: [],
                decimalPlaces: 2,
                enableDecimal: false,
                reverseMode: false,
                negativeLogic: false
            }
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return PercentageGaugeWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: PercentageGaugeData = PercentageGaugeWidget.GetDefaultWidgetDataStatic();
        return {
            Title: "Percentage Gauge",
            WidgetTitle: "",
            Key: "PercentageGaugeWidget",
            Type: "PercentageGaugeWidget",
            Data: data,
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/png/percentage_gauge.png"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        if (IsNullOrEmpty(this.Description.WidgetTitle))
            return {
                [WidgetsThingsStructs.WidgetLocalizationKeys.Title]: this.Description.WidgetTitle
            };
        else
            // nothing to expose
            return {};
    };
    // ------------------------------------------------------------------------------------------------
    GetDataPortKeyStatic(): string { return PercentageGaugePortKeys.Data; };
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================